import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { parishesApi } from '@/api/endpoints/parishes'
import { useAuth } from '@/stores/auth'
import { useUI } from '@/stores/ui'

interface SnackWarningProps {
  /** If true, always show the warning (for Admin view even when disabled) */
  forceShow?: boolean
  /** If true, show the toggle checkbox (Admin only) */
  showToggle?: boolean
}

/**
 * Snack Warning Component
 * Displays a message about avoiding petrochemical dyes/additives in snacks
 * Can be toggled by Admins to show/hide for Teachers
 */
export default function SnackWarning({ forceShow = false, showToggle = false }: SnackWarningProps) {
  const { user } = useAuth()
  const parishId = user?.parish_id
  const addToast = useUI((state) => state.addToast)
  const [showWarning, setShowWarning] = useState(true)
  const [displayToTeachers, setDisplayToTeachers] = useState(true)
  const [snackMessage, setSnackMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  
  const DEFAULT_MESSAGE = "In addition to being watchful for allergens, Teachers should avoid snacks with petrochemical dyes or additives - e.g. Red #40, Blue 5, etc., and TBHQ, BHA, or BHT - as research has shown that these ingredients have a strong tendency to make children hyperactive and lose focus."

  // Load current preference
  useEffect(() => {
    if (!showToggle || !parishId) return

    let mounted = true
    const loadPreference = async () => {
      setLoading(true)
      try {
        const parish = await parishesApi.get(parishId)
        if (mounted) {
          setDisplayToTeachers(parish.show_snack_warning_to_teachers ?? true)
          setSnackMessage(parish.snack_warning_message || null)
          // Show warning if forced, or if enabled and user is not a teacher, or if enabled and user is a teacher
          setShowWarning(forceShow || (parish.show_snack_warning_to_teachers ?? true))
        }
      } catch (error) {
        console.error('Failed to load snack warning preference:', error)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    loadPreference()
    return () => { mounted = false }
  }, [parishId, showToggle, forceShow])

  // For Teachers: check if warning is enabled
  useEffect(() => {
    if (showToggle || !parishId || user?.role !== 'TEACHER') return

    let mounted = true
    const loadPreference = async () => {
      try {
        const parish = await parishesApi.get(parishId)
        if (mounted) {
          setShowWarning(parish.show_snack_warning_to_teachers ?? true)
          setSnackMessage(parish.snack_warning_message || null)
        }
      } catch (error) {
        console.error('Failed to load snack warning preference:', error)
      }
    }
    loadPreference()
    return () => { mounted = false }
  }, [parishId, showToggle, user?.role])

  const handleToggle = async (enabled: boolean) => {
    if (!parishId || saving) return

    setSaving(true)
    try {
      await parishesApi.update(parishId, {
        show_snack_warning_to_teachers: enabled,
      })
      setDisplayToTeachers(enabled)
      setShowWarning(forceShow || enabled)
      addToast({
        message: enabled 
          ? 'Snack warning will now be displayed to Teachers'
          : 'Snack warning will be hidden from Teachers',
        type: 'success',
      })
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to update snack warning preference',
        type: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  // Don't show anything if warning is disabled and not forced
  if (!showWarning && !forceShow) {
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mt-3 p-3 rounded-lg border ${
        forceShow && !displayToTeachers
          ? 'bg-gray-100 border-gray-300 text-gray-500'
          : 'bg-yellow-50 border-yellow-200'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className={`text-sm ${forceShow && !displayToTeachers ? 'text-gray-500' : 'text-gray-700'}`}>
          <span className="font-medium">Note on snacks:</span>{' '}
          {snackMessage || DEFAULT_MESSAGE}
        </p>
        
        {showToggle && (user?.role === 'PRIMARY_ADMIN' || user?.role === 'DESIGNATED_ADMIN') && (
          <label className="flex items-center gap-2 shrink-0 cursor-pointer">
            <input
              type="checkbox"
              checked={displayToTeachers}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={loading || saving}
              className="w-4 h-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-400 focus:ring-offset-0 cursor-pointer disabled:opacity-50"
            />
            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
              Display to Teachers
            </span>
          </label>
        )}
      </div>
    </motion.div>
  )
}

